import {
  appModule,
  navModule,
  catalogEntityPresentationModule,
  catalogExportModule,
  catalogLayoutModule,
} from './modules';
import { guidePlugin } from './plugins';
import { apiDocsPlugin } from '@backstage/plugin-api-docs';
import { default as catalogPlugin } from '@backstage/plugin-catalog/alpha';
import {
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { orgPlugin } from '@backstage/plugin-org';
import {
  techdocsPlugin,
} from '@backstage/plugin-techdocs';
import { default as techdocsAlphaPlugin } from '@backstage/plugin-techdocs/alpha';
import { createApp } from '@backstage/frontend-defaults';
import {
  coreExtensionData,
  createExtensionDataRef,
} from '@backstage/frontend-plugin-api';
import type { CatalogExportSettings } from '@backstage/plugin-catalog';
import { Container } from '@backstage/ui';
import { PropsWithChildren } from 'react';
import { z } from 'zod/v4';
import { compatWrapper, convertLegacyRouteRef, convertLegacyRouteRefs } from '@backstage/core-compat-api';

import { HomePage } from './components/home/HomePage';

import {
  catalogColumnsFunc,
} from '@teamsystem/backstage-plugin-catalog-teamsystem-components';

import homePlugin from '@backstage/plugin-home/alpha';
import techRadarPlugin from '@backstage-community/plugin-tech-radar/alpha';

import techDocsPlugin from '@backstage/plugin-techdocs/alpha';
import { techDocsMermaidAddonModule } from "backstage-plugin-techdocs-addon-mermaid";

/**
 * The home page is the TeamSystem `HomePage` instead of the widget board the
 * home plugin renders by default, mounted on `/` rather than on `/home`.
 *
 * `noHeader: false` is the third param and not a leftover: the stock
 * `page:home` passes `noHeader: true` to `PageBlueprint`, and override params
 * are merged over the original ones (`{...base, ...overrides}`), so the flag
 * survives an override that does not restate it — the page would be the only
 * one in the portal without the `PluginHeader`. Upstream sets it because its
 * default home page draws its own greeting header; ours does not, so the flag
 * has to go. See docs/frontend-system-migration.md.
 */
const homePluginOverride = homePlugin.withOverrides({
  extensions: [
    homePlugin.getExtension('page:home').override({
      params: {
        path: '/',
        noHeader: false,
        loader: async () => <HomePage />,
      },
    }),
  ],
});

/**
 * The catalog index page is the stock one — `CatalogIndexPage` of
 * `@backstage/plugin-catalog/alpha` is `NfsDefaultCatalogPage`, the Backstage
 * UI shell the `page:catalog` extension renders by default (BUI `Header` and
 * `Container`, create button behind `catalogEntityCreatePermission`, export
 * button, support button, translated title).
 *
 * It is overridden for one reason: `columns`. The catalog table columns are
 * the last part of that page which is still a component prop rather than an
 * extension, so there is no way to inject `catalogColumnsFunc` through an
 * input or through `app.extensions`. Upstream tracks this as
 * backstage/backstage#33438; the `CatalogColumnBlueprint` that would fix it
 * (PR #34397) is open with changes requested, and the core maintainers'
 * answer on it is that overriding the page is the supported stop-gap.
 *
 * The override is written as a `factory` and not as `params` on purpose: only
 * the factory form receives `inputs` and `config`, which is what lets the page
 * keep behaving like the stock one —
 *
 * - `inputs.filters` are the `catalog-filter:*` extensions, so the filter
 *   column stays extension driven and configurable (which filters, in which
 *   order) from `app.extensions`, instead of being a hardcoded JSX list;
 * - `config.pagination` and `config.exportSettings` keep working as documented.
 *
 * `originalFactory` merges the params it is given over the ones the catalog
 * plugin passes itself, so `path`, `routeRef`, `title` and `icon` are still
 * the catalog's own — every `catalogIndex` route binding keeps resolving.
 *
 * - `inputs.exportConfig` are the `CatalogExportConfigBlueprint` extensions,
 *   merged exactly the way the stock factory merges them, so the YAML exporter
 *   of `catalogExportModule` reaches the dialog through the documented route
 *   rather than being wired into this file.
 */

/**
 * The catalog plugin does not export the data ref behind its `exportConfig`
 * input — `CatalogExportConfigBlueprint` is created without `dataRefs` — so the
 * only way to read that input is to declare a ref with the same id. Extension
 * data containers resolve purely by id (`container.get(ref.id)`), and the id is
 * part of the extension's public type, so this is stable in a way that reaching
 * into internals would not be. Declaring it here rather than dropping the input
 * is what keeps `CatalogExportConfigBlueprint` working for any plugin, not just
 * for our own module.
 */
const catalogExportConfigDataRef = createExtensionDataRef<{
  exporters?: CatalogExportSettings['exporters'];
  columns?: CatalogExportSettings['columns'];
  onSuccess?: CatalogExportSettings['onSuccess'];
  onError?: CatalogExportSettings['onError'];
}>().with({ id: 'catalog.export-customization' });
const catalogPluginOverride = catalogPlugin.withOverrides({
  extensions: [
    catalogPlugin.getExtension('page:catalog').override({
      factory(originalFactory, { inputs, config }) {
        return originalFactory({
          params: {
            loader: async () => {
              const { CatalogIndexPage } = await import(
                '@backstage/plugin-catalog/alpha'
              );
              const filters = inputs.filters.map(filter =>
                filter.get(coreExtensionData.reactElement),
              );

              const exportSettings: CatalogExportSettings = {
                ...config.exportSettings,
              };
              for (const exportConfig of inputs.exportConfig) {
                const data = exportConfig.get(catalogExportConfigDataRef);
                if (!data) {
                  continue;
                }
                if (data.exporters) {
                  exportSettings.exporters = {
                    ...exportSettings.exporters,
                    ...data.exporters,
                  };
                }
                if (data.columns && !exportSettings.columns) {
                  exportSettings.columns = data.columns;
                }
                if (data.onSuccess && !exportSettings.onSuccess) {
                  exportSettings.onSuccess = data.onSuccess;
                }
                if (data.onError && !exportSettings.onError) {
                  exportSettings.onError = data.onError;
                }
              }

              return (
                <CatalogIndexPage
                  columns={catalogColumnsFunc}
                  filters={<>{filters}</>}
                  pagination={config.pagination}
                  exportSettings={
                    exportSettings.enabled ? exportSettings : undefined
                  }
                />
              );
            },
          },
        });
      },
    }),
  ],
});

/**
 * TechDocs used to be mounted as `<TechDocsIndexPage initialFilter="all"
 * ownerPickerMode="all" />`. `page:techdocs` is a plain `PageBlueprint.make`
 * with a hardcoded loader and no config of its own, so the stock page defaults
 * the user filter to `owned` and the owner picker to `owners-only` — the docs
 * index opened showing only the signed in user's entities. Unlike
 * `page:api-docs`, which exposes `initiallySelectedFilter`, there is nothing to
 * configure, so the two knobs are added to the extension here — `override`
 * accepts a `configSchema` that is merged into the original one — and their
 * values live in `app-config.yaml` like every other extension setting.
 *
 * The page itself is the public `TechDocsIndexPage`. It is the legacy shell by
 * default (`TechDocsPageWrapper` → MUI `PageWithHeader`), which would sit badly
 * under the `PluginHeader` every page already gets, so `PageWrapper` and
 * `CustomHeader` — both public props meant for exactly this — swap in the BUI
 * `Container` the new frontend system's own TechDocs page uses. The remaining
 * difference from that page is one MUI `Content` of padding; reproducing it
 * exactly would mean forking `TechDocsIndexPageContent`, which the package does
 * not export, and re-forking a page is what this branch just undid.
 *
 * Both wrappers are declared at module scope on purpose: inline component
 * definitions are a new component type on every render and would remount the
 * whole filter tree.
 */
const TechDocsContainer = ({ children }: PropsWithChildren) => (
  <Container mt="6">{children}</Container>
);

const NoHeader = () => null;

const techdocsPluginOverride = techdocsAlphaPlugin.withOverrides({
  extensions: [
    techdocsAlphaPlugin.getExtension('page:techdocs').override({
      configSchema: {
        initialFilter: z.enum(['owned', 'starred', 'all']).default('all'),
        ownerPickerMode: z.enum(['owners-only', 'all']).default('all'),
      },
      factory(originalFactory, { config }) {
        return originalFactory({
          params: {
            loader: async () => {
              const { TechDocsIndexPage } = await import(
                '@backstage/plugin-techdocs'
              );
              return (
                <TechDocsIndexPage
                  initialFilter={config.initialFilter}
                  ownerPickerMode={config.ownerPickerMode}
                  PageWrapper={TechDocsContainer}
                  CustomHeader={NoHeader}
                />
              );
            },
          },
        });
      },
    }),
  ],
});

/**
 * The tech radar page is the stock `page:tech-radar` with one thing replaced:
 * its loader. `RadarPage`, the component the extension renders by default,
 * hardcodes a MUI `Page` + `Header` and exposes no prop to suppress it — so
 * that page was the last one showing the dark blue legacy header stacked under
 * the `PluginHeader`. `components/tech-radar/TechRadarContent.tsx` is the same
 * body without the shell; see the comment there for what it reproduces.
 *
 * The override is a `factory` because the loader needs `config`: `subtitle`,
 * `pageTitle`, `width` and `height` are the extension's own config keys, and
 * the stock loader passes them straight into `RadarPage`. `originalFactory`
 * merges our params over the plugin's, so `path`, `title`, `icon` and the
 * route ref stay the plugin's own.
 *
 * `compatWrapper` is kept for parity with the stock loader — `TechRadarComponent`
 * only needs `useApi` today, but it is a legacy component and this is what
 * upstream wraps it in.
 */
const techRadarPluginOverride = techRadarPlugin.withOverrides({
  extensions: [
    techRadarPlugin.getExtension('page:tech-radar').override({
      factory(originalFactory, { config }) {
        return originalFactory({
          params: {
            loader: async () => {
              const { TechRadarContent } = await import(
                './components/tech-radar/TechRadarContent'
              );
              return compatWrapper(
                <TechRadarContent
                  subtitle={config.subtitle}
                  pageTitle={config.pageTitle}
                  width={config.width}
                  height={config.height}
                />,
              );
            },
          },
        });
      },
    }),
  ],
});

const app = createApp({
  features: [
    homePluginOverride,
    catalogPluginOverride,
    catalogExportModule,
    techDocsPlugin,
    techDocsMermaidAddonModule,
    techdocsPluginOverride,
    techRadarPluginOverride,
    guidePlugin,
    appModule,
    navModule,
    catalogEntityPresentationModule,
    catalogLayoutModule,
  ],
  bindRoutes: ({ bind }) => {
    bind(convertLegacyRouteRefs(catalogPlugin.externalRoutes), {
      createComponent: convertLegacyRouteRef(scaffolderPlugin.routes.root),
      viewTechDoc: convertLegacyRouteRef(techdocsPlugin.routes.docRoot),
      createFromTemplate: convertLegacyRouteRef(scaffolderPlugin.routes.selectedTemplate),
    });
    bind(convertLegacyRouteRefs(apiDocsPlugin.externalRoutes), {
      registerApi: convertLegacyRouteRef(catalogImportPlugin.routes.importPage),
    });
    bind(convertLegacyRouteRefs(scaffolderPlugin.externalRoutes), {
      registerComponent: convertLegacyRouteRef(catalogImportPlugin.routes.importPage),
      viewTechDoc: convertLegacyRouteRef(techdocsPlugin.routes.docRoot),
    });
    bind(convertLegacyRouteRefs(orgPlugin.externalRoutes), {
      catalogIndex: convertLegacyRouteRef(catalogPlugin.routes.catalogIndex),
    });
  },
});

export default app.createRoot();